<script setup lang="ts">
/**
 * ═══════════════════════════════════════════════════════════════════════════
 *  СКОУП ОДНОГО МЕРОПРИЯТИЯ
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Компонент маршрута `/events/:eventId`, родитель всех экранов внутри
 * соревнования. Делает ровно три вещи, и каждая из них должна быть сделана
 * **один раз на мероприятие**, а не по разу на экран:
 *
 *   1. грузит мероприятие и его состав — из состава считается моя роль;
 *   2. грузит четыре справочника (роли участников, дополнительные поля, зоны
 *      доступа, уровни доступа) — без первых двух нельзя отрисовать таблицу
 *      участников, а зоны и уровни нужны сразу двум экранам и обоим целиком
 *      (см. ниже);
 *   3. показывает шапку мероприятия с названием, датами, статусом и моей
 *      ролью в нём.
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ПОЧЕМУ СПРАВОЧНИКИ ГРУЗЯТСЯ ЗДЕСЬ, А НЕ В ЭКРАНЕ УЧАСТНИКОВ
 * ───────────────────────────────────────────────────────────────────────────
 *
 * Их нужно четырём экранам сразу (список, форма создания, форма правки,
 * импорт), и живут они дольше любого из них. Загрузка «в экране» означала бы
 * четыре одинаковых запроса на каждый переход между вкладками — и мигающие
 * колонки таблицы, пока ответ в пути.
 *
 * ⚠️ ЗОНЫ ДОСТУПА ПЕРЕЕХАЛИ СЮДА ИЗ СВОЕГО ЭКРАНА, и вот почему. Пока зоны
 * были нужны только их собственному справочнику, они грузились там же. Теперь
 * их нужно ДВУМ экранам, и оба зависят от полного списка:
 *
 *   «Роли и поля»   рисует под каждой ролью все зоны площадки с галочками;
 *   «Зоны доступа»  на удалении обязан сказать, у скольких ролей зона
 *                   пропадёт, — а это считается из справочника РОЛЕЙ.
 *
 * То есть каждому из двух экранов нужны ОБА справочника, и держать загрузку в
 * экранах значило бы, что каждый грузит чужой. Здесь они грузятся один раз.
 *
 * Уровни доступа появились позже и попали сюда сразу, по этой же цепочке: они
 * ведут себя как зоны во всём, кроме содержания записи (цифра вместо
 * триграммы с цветом).
 *
 * ───────────────────────────────────────────────────────────────────────────
 *  ⚠️ ЧЕТЫРЕ СОСТОЯНИЯ, И ТРЕТЬЕ ЛЕГКО ПРОПУСТИТЬ
 * ───────────────────────────────────────────────────────────────────────────
 *
 *   • загрузка          — скелетон;
 *   • ошибка            — мероприятия не видно. ⚠️ Это **и** «нет такого id»,
 *                         **и** «вас не добавили в состав»: сервер намеренно
 *                         отвечает 404 в обоих случаях, чтобы перебор id не
 *                         превращался в перепись мероприятий. Текст обязан
 *                         называть обе причины — иначе половина людей будет
 *                         искать несуществующую опечатку в ссылке;
 *   • загружено         — рисуем шапку и `RouterView`;
 *   • сменился `eventId` — новый скоуп, всё грузится заново. Watcher, а не
 *                         `onMounted`: layout переиспользуется при переходе
 *                         между двумя мероприятиями и НЕ пересоздаётся.
 */
import { CalendarRange } from '@lucide/vue'
import { watch } from 'vue'

import MemberRoleBadge from '@/components/common/MemberRoleBadge.vue'
import EventStatusBadge from '@/components/common/EventStatusBadge.vue'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty'
import { useAuth } from '@/composables/useAuth'
import { useEventAccess } from '@/composables/useEventAccess'
import { formatDate } from '@/lib/format'
import { useAccessLevelsStore } from '@/stores/access-levels'
import { usePictogramsStore } from '@/stores/pictograms'
import { useAccessZonesStore } from '@/stores/access-zones'
import { useBadgeTemplateStore } from '@/stores/badge-template'
import { useEventStore } from '@/stores/event'
import { useParticipantFieldsStore } from '@/stores/participant-fields'
import { useParticipantRolesStore } from '@/stores/participant-roles'

const props = defineProps<{
  /** Приходит из `props: true` маршрута — вью не лезет в `useRoute()`. */
  eventId: string
}>()

const eventStore = useEventStore()
const fieldsStore = useParticipantFieldsStore()
const rolesStore = useParticipantRolesStore()
const zonesStore = useAccessZonesStore()
/**
 * Уровни доступа — по тем же двум причинам, что и зоны, и ровно с той же
 * парой экранов: «Роли и поля» рисует под каждой ролью весь справочник
 * галочками, а «Уровни доступа» на удалении обязан сказать, у каких ролей
 * уровень пропадёт (это считается из справочника РОЛЕЙ).
 *
 * ⚠️ Второй справочник, а не замена зонам: зона говорит, куда пускают, уровень
 * — насколько глубоко. Роль держит оба набора сразу.
 */
const levelsStore = useAccessLevelsStore()
/**
 * Пиктограммы — по тем же двум причинам, что зоны и уровни, и ровно с той же
 * парой экранов: «Роли и поля» рисует под каждой ролью весь справочник
 * галочками, а «Пиктограммы» на удалении обязан сказать, у каких ролей запись
 * пропадёт (это считается из справочника РОЛЕЙ).
 *
 * ⚠️ Третий справочник, а не замена двум: зона — «куда», уровень — «насколько
 * глубоко», пиктограмма — «что положено». Роль держит все три набора сразу.
 *
 * ⚠️ Его `load` тянет ещё и БАЙТЫ ИКОНОК, поэтому предзагрузка здесь стоит
 * дороже остальных справочников. Она всё равно здесь: без неё оба экрана
 * показывали бы пустые квадраты в момент открытия, а картинки маленькие
 * (длинная сторона ограничена 512 px) и кэшируются на всё время жизни скоупа.
 */
const pictogramsStore = usePictogramsStore()
/**
 * Шаблон бейджа грузится здесь, а не в своём экране, по тому же правилу, что и
 * справочники: он нужен ДВУМ разделам — конструктору и печати из списка
 * участников, — и нужен целиком. Без предзагрузки кнопка «Печать бейджей» в
 * списке участников не знала бы, есть ли вообще что печатать, до первого клика.
 */
const badgeStore = useBadgeTemplateStore()

const { event, myMemberRole, canManage, canEditScanFields, loading, error } = useEventAccess()
const { isAdmin } = useAuth()

/**
 * Загрузка скоупа. `immediate: true` заменяет `onMounted` — так первый вход и
 * смена мероприятия идут по одному и тому же коду.
 *
 * Запросы уходят параллельно (внутри `load` их два, плюс три справочника): они
 * независимы, и последовательная цепочка умножила бы задержку до первого
 * отрисованного экрана.
 *
 * Ни один из них не бросает — каждый кладёт ошибку в свой стор. `void` здесь
 * означает «результат не нужен», а не «ошибку глотаем».
 */
watch(
  () => props.eventId,
  (id) => {
    if (!id) return
    void eventStore.load(id)
    void fieldsStore.load(id)
    void rolesStore.load(id)
    void zonesStore.load(id)
    void levelsStore.load(id)
    void pictogramsStore.load(id)
    void badgeStore.load(id)
  },
  { immediate: true },
)
</script>

<template>
  <!--
    Загрузка: скелетон повторяет форму шапки, чтобы контент не прыгал.

    ⚠️ Условие — `loading && !event`, а не `!ready && loading`. Разница видна на
    кнопке «Повторить» после ошибки: там `ready` уже `true`, `error` только что
    сброшен, а `event` всё ещё `null` — и с прежним условием ни одна ветка не
    подходила, экран уходил в белое до самого ответа.

    Заодно это убирает мигание при перезагрузке уже открытого мероприятия:
    пока данные есть, показываем их, а не скелетон поверх готового экрана.
  -->
  <div v-if="loading && !event" class="space-y-6">
    <div class="space-y-2">
      <Skeleton class="h-8 w-72" />
      <Skeleton class="h-4 w-96" />
    </div>
    <Skeleton class="h-64 w-full" />
  </div>

  <!--
    Мероприятия не видно. Текст называет обе причины намеренно — см. шапку
    файла: сервер не различает «нет такого» и «вы не в составе», и врать за
    него, выбрав одну формулировку, нельзя.
  -->
  <div v-else-if="error" class="flex min-h-[60vh] w-full items-center justify-center p-6">
    <Empty class="max-w-md border-0">
      <EmptyHeader>
        <EmptyMedia>
          <CalendarRange class="size-16 text-muted-foreground" />
        </EmptyMedia>
        <EmptyTitle>Мероприятие недоступно</EmptyTitle>
        <EmptyDescription>
          {{ error.message }}
          <template v-if="error.isEventGone">
            <br>
            Проверьте ссылку или попросите владельца мероприятия добавить вас в состав.
          </template>
        </EmptyDescription>
      </EmptyHeader>

      <EmptyContent>
        <div class="flex flex-wrap items-center justify-center gap-2">
          <Button @click="$router.push({ name: 'events' })">
            К списку мероприятий
          </Button>
          <Button variant="outline" @click="eventStore.load(props.eventId, true)">
            Повторить
          </Button>
        </div>
      </EmptyContent>
    </Empty>
  </div>

  <div v-else-if="event" class="flex flex-1 flex-col gap-6">
    <!--
      Шапка мероприятия. Она нужна на КАЖДОМ экране внутри скоупа: две вкладки
      с разными соревнованиями иначе отличаются только адресной строкой, и
      «удалил не тех участников» становится вопросом времени.
    -->
    <header
      aria-label="Текущее мероприятие"
      class="flex flex-col gap-3 border-b pb-4 sm:flex-row sm:items-start sm:justify-between"
    >
      <div class="min-w-0 space-y-1">
        <div class="flex flex-wrap items-center gap-2">
          <!--
            ⚠️ Не `<h1>`, хотя выглядит как заголовок. `h1` на экране ровно
            один — его рисует `PageHeader` дочернего маршрута («Участники»,
            «Состав»). Два `h1` ломают навигацию по заголовкам у скринридера:
            «Кубок области» и «Участники» встают на один уровень, и структура
            «где я → что здесь» исчезает.

            Название мероприятия при этом остаётся доступным: регион
            подписан `aria-label`, и по нему можно перейти как по ориентиру.
          -->
          <p class="truncate text-xl font-semibold tracking-tight">
            {{ event.name }}
          </p>
          <EventStatusBadge :status="event.status" />
        </div>
        <p class="text-sm text-muted-foreground">
          {{ formatDate(event.startsAt) }} — {{ formatDate(event.endsAt) }}
        </p>
      </div>

      <!--
        Моя роль в этом мероприятии. Показана всегда и намеренно: наблюдатель,
        который не понимает, почему у него нет кнопок, — самый частый вопрос
        к поддержке, и ответ должен быть на экране, а не в переписке.

        У администратора роли в составе нет вовсе (он в нём не числится),
        поэтому для него отдельный бейдж — иначе получилось бы пустое место
        там, где у всех остальных написано, кто они здесь.
      -->
      <div class="flex shrink-0 items-center gap-2">
        <Badge v-if="isAdmin && !myMemberRole" variant="default">
          Администратор системы
        </Badge>
        <MemberRoleBadge v-else-if="myMemberRole" :role="myMemberRole" />
        <!--
          ⚠️ «Наблюдателю+» плашка «Только чтение» солгала бы: графы,
          подсвеченные для сканирования, он как раз правит — и в сканере, и в
          карточке участника. Прочитав «только чтение», он не станет и
          пробовать, а роль заведена ровно ради этого действия.
        -->
        <Badge
          v-if="canEditScanFields && !canManage"
          variant="outline"
          class="text-muted-foreground"
        >
          Только подсвеченные графы
        </Badge>
        <Badge v-else-if="!canManage" variant="outline" class="text-muted-foreground">
          Только чтение
        </Badge>
      </div>
    </header>

    <RouterView />
  </div>
</template>
